import { useState, useCallback } from 'react';
import { useJobCacheInvalidation } from '../../hooks/useJobCacheInvalidation';
import {
  Container,
  Box,
  Typography,
  Alert,
} from '@mui/material';
// Icon imports removed since navigation is now in header
import type { JobResponse } from '@taxassist/api-client';
import { KanbanBoard } from '../../components/kanban/KanbanBoard';
import { CreateJobFAB } from '../../components/common/CreateJobFAB';
import { CreateJobDialog } from '../../components/jobs/CreateJobDialog';
import { FileUploadDialog } from '../../components/jobs/FileUploadDialog';
import { JobDetailsDrawer } from '../../components/jobs/JobDetailsDrawer';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import {
  NotificationSnackbar,
  useNotification,
} from '../../components/common/NotificationSnackbar';
import { useCurrentFirmId } from '../../contexts/FirmContext';
import { useJobs } from '../../hooks/useJobs';
import type { FileUploadItem } from '../../hooks/useFileUpload';

export function KanbanJobsView() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedJobForUpload, setSelectedJobForUpload] = useState<JobResponse | null>(
    null
  );
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<
    string | null
  >(null);
  const { invalidateJobRelatedQueries } = useJobCacheInvalidation();
  const {
    successMessage,
    errorMessage,
    showSuccess,
    showError,
    clearSuccess,
    clearError,
  } = useNotification();
  const currentFirmId = useCurrentFirmId();

  // Use custom hook for data fetching with consistent configuration
  const { data: jobs = [], isLoading, error } = useJobs();

  const handleJobCreated = (jobId: string) => {
    showSuccess(`Job ${jobId} created successfully!`);
    // The useCreateJob hook will automatically invalidate and refetch
  };

  const handleUploadFiles = (job: JobResponse) => {
    setSelectedJobForUpload(job);
    setUploadDialogOpen(true);
  };

  const handleViewJobDetails = (job: JobResponse) => {
    setSelectedJobForDetails(job.jobId);
    setDetailsDrawerOpen(true);
  };

  const handleUploadComplete = useCallback(async (files: FileUploadItem[]) => {
    showSuccess(`Successfully uploaded ${files.length} file(s)`);
    
    // Invalidate job-related queries to update all job lists
    // This will automatically trigger refetch for active queries
    await invalidateJobRelatedQueries({
      firmId: currentFirmId || undefined,
      jobId: selectedJobForUpload?.jobId
    });
  }, [showSuccess, invalidateJobRelatedQueries, currentFirmId, selectedJobForUpload?.jobId]);

  const handleUploadError = (error: Error) => {
    showError(error.message);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedJobForUpload(null);
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedJobForDetails(null);
  };

  if (error) {
    return (
      <Container>
        <Box py={4}>
          <Alert severity="error">
            Failed to load jobs. Please try again later.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Show loading state if no firm selected or initial load
  if (!currentFirmId || (isLoading && !jobs.length)) {
    return <LoadingSpinner message="Loading kanban board..." />;
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 10 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Page Header */}
        <Box mb={3}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Jobs Kanban Board
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visual workflow management for tax return processing
          </Typography>
        </Box>

        {/* Kanban Board */}
        <KanbanBoard
          jobs={jobs}
          isLoading={isLoading}
          onCreateJob={() => setCreateDialogOpen(true)}
          onUploadFiles={handleUploadFiles}
          onViewDetails={handleViewJobDetails}
        />

        {/* Floating Action Button */}
        <CreateJobFAB onClick={() => setCreateDialogOpen(true)} />

        {/* Create Job Dialog */}
        {currentFirmId && (
          <CreateJobDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            firmId={currentFirmId}
            onJobCreated={handleJobCreated}
          />
        )}

        {/* File Upload Dialog */}
        <FileUploadDialog
          open={uploadDialogOpen}
          onClose={handleCloseUploadDialog}
          mode="general"
          job={selectedJobForUpload}
          onUploadComplete={handleUploadComplete}
          onError={handleUploadError}
        />

        {/* Job Details Drawer */}
        {selectedJobForDetails && (
          <JobDetailsDrawer
            open={detailsDrawerOpen}
            onClose={handleCloseDetailsDrawer}
            jobId={selectedJobForDetails}
          />
        )}

        {/* Success Notification */}
        <NotificationSnackbar
          message={successMessage}
          onClose={clearSuccess}
          severity="success"
        />

        {/* Error Notification */}
        <NotificationSnackbar
          message={errorMessage}
          onClose={clearError}
          severity="error"
        />
      </Container>
    </Box>
  );
}